import { Box, Container, styled, SxProps } from '@mui/material'
import { useState, useRef, useEffect, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { appBarHeight } from '@shared/constants'

const Wrapper = styled(Box)(({ theme }) => ({
  backgroundColor: '#E8F1FF',
  display: 'flex',
  alignItems: 'center',
  padding: '2px 16px',
  height: '44px',
  position: 'sticky',
  top: `${appBarHeight.xs}px`, // For mobile screens
  zIndex: theme.zIndex.appBar - 1, // Just below app-bar
  width: '100%',
  overflow: 'hidden',
  overflowX: 'scroll',
  // Hide scrollbar
  scrollbarWidth: 'none', // Firefox
  msOverflowStyle: 'none', // IE and Edge
  '&::-webkit-scrollbar': {
    display: 'none', // Chrome, Safari and Opera
  },
  [theme.breakpoints.up('md')]: {
    top: `${appBarHeight.md}px`, // For desktop screens
  },
}))

const StyledContainer = styled(Container)(() => ({
  display: 'flex',
  alignItems: 'center',
  position: 'relative',
}))

interface IOneItemProps {
  isActive: boolean
}

const OneItem = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isActive',
})<IOneItemProps>(({ isActive, theme }) => ({
  ...theme.typography.button,
  color: isActive ? theme.palette.text.white : theme.palette.text.primary,
  padding: '10px 32px',
  cursor: 'pointer',
  zIndex: 1,
  transition: 'color 0.3s ease',
  textWrap: 'nowrap',
  '&:hover': {
    textDecoration: isActive ? 'none' : 'underline',
  },
}))

interface IBackgroundItemProps {
  leftPosition: number
  width: number
  isVisible: boolean
}

const BackgroundItem = styled(Box, {
  shouldForwardProp: (prop) =>
    prop !== 'leftPosition' && prop !== 'width' && prop !== 'isVisible',
})<IBackgroundItemProps>(({ leftPosition, width, isVisible, theme }) => ({
  backgroundColor: theme.palette.secondary.main,
  height: '40px',
  width: `${width}px`,
  position: 'absolute',
  left: `${leftPosition}px`,
  top: '2px',
  borderRadius: '4px',
  opacity: isVisible ? 1 : 0,
  transition: 'left 0.3s ease, width 0.3s ease, opacity 0.3s ease',
  [theme.breakpoints.up('md')]: {
    height: '40px',
  },
}))

interface IItem {
  label: string
  id: string
}

interface IProps {
  sx?: SxProps
  items?: IItem[]
}

export function AnchorNavigation({ sx, items }: IProps) {
  const [activeItem, setActiveItem] = useState<string | null>(null)
  const { t, i18n } = useTranslation('features')

  // Flag to prevent intersection observer updates during programmatic scroll
  const isScrollingProgrammatically = useRef(false)
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const lastScrollTop = useRef(0)

  // Debounced setActiveItem function
  const debouncedSetActiveItem = useMemo(() => {
    let timeoutId: NodeJS.Timeout

    const debouncedFn = (id: string) => {
      clearTimeout(timeoutId)

      timeoutId = setTimeout(() => setActiveItem(id), 100)
    }

    // Return function with cleanup method
    return Object.assign(debouncedFn, {
      cleanup: () => clearTimeout(timeoutId),
    })
  }, [])

  const itemsBase: IItem[] = [
    { label: t('anchor-navigation.overview'), id: 'fund-overview' },
    { label: t('anchor-navigation.team'), id: 'fund-team' },
    { label: t('anchor-navigation.fund facts'), id: 'fund-facts' },
    { label: t('anchor-navigation.performance'), id: 'history-chart' },
    { label: t('anchor-navigation.characteristics'), id: 'characteristics' },
    { label: t('anchor-navigation.projects'), id: 'projects' },
  ]

  const itemsForRender = items || itemsBase

  const [backgroundPosition, setBackgroundPosition] = useState({
    left: 0,
    width: 0,
  })

  const itemRefs = useRef<{ [key: string]: HTMLDivElement | null }>({})
  const containerRef = useRef<HTMLDivElement>(null)

  const updateBackgroundPosition = useCallback(
    (itemId: string) => {
      const itemElement = itemRefs.current[itemId]
      const containerElement = containerRef.current

      if (itemElement && containerElement) {
        const itemRect = itemElement.getBoundingClientRect()
        const containerRect = containerElement.getBoundingClientRect()

        // Calculate position relative to container, considering scroll offset
        const leftPosition = itemRect.left - containerRect.left - 16 // 16px padding to center the background
        const width = itemRect.width + 32 // Add 32px total padding (16px on each side)

        // Ensure the background doesn't go outside the container bounds
        const maxLeftPosition = containerElement.scrollWidth - width - 32 // Account for container padding

        // For the last element, ensure proper alignment
        const isLastElement =
          itemsForRender[itemsForRender.length - 1]?.id === itemId

        let finalLeftPosition = Math.max(
          0,
          Math.min(leftPosition, maxLeftPosition)
        )

        if (isLastElement) {
          // For the last element, ensure the background aligns with the text
          const lastElementRight = itemRect.left + itemRect.width
          const containerRight = containerRect.left + containerRect.width

          // If the last element is near the right edge, adjust background position
          if (lastElementRight > containerRight - 50) {
            // Position background to align with the last element's text
            finalLeftPosition = itemRect.left - containerRect.left - 16
          }
        }

        setBackgroundPosition({ left: finalLeftPosition, width })
      }
    },
    [itemsForRender]
  )

  // Scroll to element function
  const scrollToElement = useCallback((id: string) => {
    const element = document.getElementById(id)

    if (element) {
      const yOffset = -(appBarHeight.md + 44 + 20) // App bar height + anchor nav height + margin

      const y =
        element.getBoundingClientRect().top + window.pageYOffset + yOffset

      window.scrollTo({
        top: y,
        behavior: 'smooth',
      })
    }
  }, [])

  // Function to reset scrolling flag when scroll ends
  const resetScrollingFlag = useCallback(() => {
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current)
    }

    scrollTimeoutRef.current = setTimeout(() => {
      isScrollingProgrammatically.current = false

      scrollTimeoutRef.current = null
    }, 150) // Reset flag if no scroll activity for 150ms
  }, [])

  // Listen for scroll events to detect when programmatic scroll ends
  useEffect(() => {
    const handleScroll = () => {
      if (isScrollingProgrammatically.current) {
        resetScrollingFlag()
      }

      lastScrollTop.current = window.pageYOffset
    }

    window.addEventListener('scroll', handleScroll, { passive: true })

    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [resetScrollingFlag])

  // Intersection Observer to detect which section is visible
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: `-${appBarHeight.md + 44 + 50}px 0px -50% 0px`, // Account for sticky navigation
      threshold: 0,
    }

    const observerCallback = (entries: IntersectionObserverEntry[]) => {
      // Ignore intersection updates during programmatic scroll
      if (isScrollingProgrammatically.current) {
        return
      }

      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const id = entry.target.id

          if (itemsForRender.some((item) => item.id === id)) {
            debouncedSetActiveItem(id)
          }
        }
      })
    }

    const observer = new IntersectionObserver(observerCallback, observerOptions)

    // Observe all sections with ids from navigation
    itemsForRender.forEach((item) => {
      const element = document.getElementById(item.id)

      if (element) {
        observer.observe(element)
      }
    })

    return () => {
      observer.disconnect()

      debouncedSetActiveItem.cleanup()

      // Clear scroll timeout if component unmounts
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current)

        scrollTimeoutRef.current = null
      }
    }
  }, [itemsForRender, debouncedSetActiveItem])

  useEffect(() => {
    if (activeItem) {
      updateBackgroundPosition(activeItem)

      // Auto-scroll to center active item on mobile
      const activeElement = itemRefs.current[activeItem]
      const containerElement = containerRef.current

      if (activeElement && containerElement) {
        // Get the wrapper element (the scrollable container)
        const wrapperElement = containerElement.parentElement

        if (wrapperElement) {
          const wrapperRect = wrapperElement.getBoundingClientRect()
          const activeRect = activeElement.getBoundingClientRect()

          // Calculate the center position of the active element relative to the wrapper
          const activeCenter = activeRect.left + activeRect.width / 2
          const wrapperCenter = wrapperRect.left + wrapperRect.width / 2

          // Calculate scroll offset to center the active element in the viewport
          let scrollOffset = activeCenter - wrapperCenter

          // Add some padding to ensure the element is not at the very edge
          const padding = 20 // 20px padding from center

          if (Math.abs(scrollOffset) < padding) {
            scrollOffset = 0 // Don't scroll if already close to center
          } else if (scrollOffset > 0) {
            scrollOffset -= padding // Move slightly left
          } else {
            scrollOffset += padding // Move slightly right
          }

          // Calculate new scroll position
          const newScrollLeft = wrapperElement.scrollLeft + scrollOffset

          // Ensure scroll position is within bounds
          const maxScrollLeft =
            wrapperElement.scrollWidth - wrapperElement.clientWidth

          const clampedScrollLeft = Math.max(
            0,
            Math.min(newScrollLeft, maxScrollLeft)
          )

          // Smooth scroll to center the active element
          wrapperElement.scrollTo({
            left: clampedScrollLeft,
            behavior: 'smooth',
          })
        }
      }
    }
  }, [activeItem])

  // Recalculate background position when language changes
  useEffect(() => {
    const handleLanguageChange = () => {
      if (!activeItem) {
        return
      }

      // Ensure DOM is updated with new translations before measuring
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          updateBackgroundPosition(activeItem)
        })
      })
    }

    i18n.on('languageChanged', handleLanguageChange)

    return () => {
      i18n.off('languageChanged', handleLanguageChange)
    }
  }, [i18n, activeItem, updateBackgroundPosition])

  const handleClick = (id: string) => {
    // Cancel any pending debounced updates
    debouncedSetActiveItem.cleanup()

    // Clear any existing timeout
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current)

      scrollTimeoutRef.current = null
    }

    // Set flag to ignore intersection observer during scroll
    isScrollingProgrammatically.current = true

    setActiveItem(id)

    scrollToElement(id)

    // The scroll listener will automatically reset the flag when scroll stops
  }

  return (
    <Wrapper sx={sx}>
      <StyledContainer maxWidth="lg" ref={containerRef}>
        {itemsForRender.map((item) => (
          <OneItem
            key={item.id}
            isActive={activeItem === item.id}
            onClick={() => handleClick(item.id)}
            ref={(el) => {
              itemRefs.current[item.id] = el as HTMLDivElement | null
            }}
          >
            {item.label}
          </OneItem>
        ))}

        <BackgroundItem
          leftPosition={backgroundPosition.left}
          width={backgroundPosition.width}
          isVisible={activeItem !== null}
        />
      </StyledContainer>
    </Wrapper>
  )
}
